import { GoogleMap, Marker } from "@react-google-maps/api";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { useTimezoneSelect } from "react-timezone-select";
import { Button } from "../../../components/buttons/button";
import { EmptyState } from "../../../components/empty";
import { EventImage } from "../../../components/event/eventImage";
import { Icon } from "../../../components/icon/icon";
import { MiniHeader } from "../../../components/typography/miniHeader";
import { RenderMarkdown } from "../../../components/typography/renderMarkdown";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { useGoogleMaps } from "../../../customeHooks/useGoogleMaps";
import {
  CreatedEvent,
  CreatedTicket,
} from "../../../redux/features/event/event.types";
import { tzOptions } from "../../../utils/constants";
import { formatDate } from "../../../utils/helpers";
import { TicketPurchase } from "../../../utils/types";
import { ChooseTickets } from "./chooseTickets";
import { PreviewOrganizerDetails } from "./organizerDetails";

export const PreviewStep = ({
  event,
  tickets,
  ticketsPurchase,
  setTicketsPurchase,
  setStep,
  canBuy,
  canShare = true,
}: {
  event: CreatedEvent;
  tickets: CreatedTicket[];
  ticketsPurchase: TicketPurchase[];
  setTicketsPurchase: (data: TicketPurchase[]) => void;
  canBuy: boolean;
  canShare?: boolean;
  setStep: (data: 1 | 2) => void;
}) => {
  const [sheetOpen, setSheetOpen] = useState<boolean>(false);
  const [placeDetails, setPlaceDetails] = useState<
    google.maps.places.PlaceResult | undefined
  >(undefined);

  const { isLoaded: isMapsLoaded, getDetails } = useGoogleMaps();

  const { parseTimezone } = useTimezoneSelect(tzOptions);

  const mapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isMapsLoaded && event.placeId) {
      getDetails({
        placeId: event.placeId,
      }).then((resp) => {
        if (typeof resp !== "string") {
          setPlaceDetails(resp);
        }
      });
    }
    // eslint-disable-next-line
  }, [isMapsLoaded]);

  return (
    <div className="relative flex w-full animate-fade-in flex-col gap-3 pb-40 md:flex-row md:flex-wrap md:items-start md:justify-center md:gap-10">
      <div className="flex w-full flex-col gap-3 md:max-w-[500px] xl:sticky xl:top-0">
        <BgWrapper>
          <EventImage dimension={472} image={event.image} />
        </BgWrapper>

        <PreviewOrganizerDetails
          event={event}
          className="hidden md:flex"
          canShare={canShare}
          showSocials={false}
        />
      </div>

      <div className="flex w-full flex-col gap-3 text-sm text-grey-950 md:max-w-[530px]">
        <BgWrapper className="flex flex-col gap-3">
          <p className="font-fraunces text-[32px] leading-[100%] tracking-[-0.04em] text-primary">
            {event?.name ?? ""}
          </p>

          <div className="text-sm text-grey-800">
            <RenderMarkdown>{event?.description ?? ""}</RenderMarkdown>
          </div>
        </BgWrapper>

        <BgWrapper className="flex items-center gap-2">
          <p className="flex flex-1 items-center gap-2 font-bold">
            <Icon
              svg="map-pin"
              width={20}
              height={20}
              className="text-primary"
            />
            {event?.location ?? ""}
          </p>

          {event.placeId && (
            <button
              type="button"
              className="w-fit text-sm font-bold text-blue"
              onClick={() => {
                // const googleMapsUrl = `https://www.google.com/maps/place/?q=place_id:${event.placeId}`;
                // window.open(googleMapsUrl, "_blank");
                mapRef?.current?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              VIEW MAP
            </button>
          )}
        </BgWrapper>

        <BgWrapper>
          <p className="flex items-center gap-2 font-bold">
            <Icon
              svg="calendar"
              width={20}
              height={20}
              className="text-primary"
            />
            {formatDate(event?.startDate, "ddd, MMMM DD, YYYY")}{" "}
            {formatDate(event?.startDate) === formatDate(event?.endDate)
              ? ""
              : `- ${formatDate(event?.endDate, "ddd, MMMM DD, YYYY")}`}
          </p>
        </BgWrapper>

        <BgWrapper>
          <p className="flex items-center gap-2 font-bold">
            <Icon svg="clock" width={20} height={20} className="text-primary" />
            {event?.startTime ?? ""} - {event?.endTime ?? ""}
            {event.timezone
              ? ` (${parseTimezone(event.timezone)?.abbrev})`
              : ""}
          </p>
        </BgWrapper>

        <PreviewOrganizerDetails
          event={event}
          className="flex md:hidden"
          canShare={canShare}
          showSocials={false}
        />

        <div
          className={classNames("md:!hidden", "transition-all duration-300", {
            "fixed inset-0 z-[998] bg-black/50": sheetOpen,
            "h-0": !sheetOpen,
          })}
          onClick={() => {
            setSheetOpen(false);
          }}
        />

        <BgWrapper
          className={classNames(
            "flex flex-col gap-4",
            "rounded-b-none md:rounded-b-2xl",
            "fixed bottom-0 left-0 right-0",
            "z-[999] md:z-0",
            "md:static",
          )}
        >
          <MiniHeader
            header="Choose Ticket"
            className={classNames("md:!flex", "transition-all duration-300", {
              flex: sheetOpen,
              hidden: !sheetOpen,
            })}
          />

          <div
            className={classNames(
              "overflow-hidden md:!h-fit",
              "transition-all duration-300",
              {
                "h-[253px] overflow-y-scroll": sheetOpen,
                "h-0": !sheetOpen,
              },
            )}
          >
            {(tickets?.length ?? 0) > 0 ? (
              <ChooseTickets
                tickets={tickets ?? []}
                ticketsPurchase={ticketsPurchase}
                setTicketsPurchase={setTicketsPurchase}
                showControls={true}
              />
            ) : (
              <EmptyState
                text="No Tickets Available"
                subtext="There aren't any tickets available for this event. Check back later."
                image="/assets/images/ticket-glass.png"
              />
            )}
          </div>

          <Button
            text="Register"
            type="button"
            onClick={() => {
              setStep(2);
            }}
            disabled={ticketsPurchase.length === 0 || !canBuy}
            className="!hidden md:!flex"
          />

          <Button
            text={sheetOpen ? "Continue" : "Get a ticket"}
            type="button"
            onClick={() => {
              if (sheetOpen) {
                setStep(2);
              } else {
                setSheetOpen(true);
              }
            }}
            disabled={
              (sheetOpen && ticketsPurchase.length === 0) ||
              (sheetOpen && !canBuy)
            }
            className="md:!hidden"
          />
        </BgWrapper>

        {event.placeId && (
          <BgWrapper
            loading={!isMapsLoaded}
            loaderHeight={288}
            className="flex flex-col gap-3"
            ref={mapRef}
          >
            <div className="flex items-center gap-3">
              <p className="rounded-lg border border-grey-100 p-[10px]">
                <Icon
                  svg="map-pin"
                  width={20}
                  height={20}
                  className="text-grey-700"
                />
              </p>

              <p className="flex flex-col">
                <span className="text-base font-bold text-primary">
                  {placeDetails?.name}
                </span>
                <span className="text-sm text-grey-600">
                  {placeDetails?.formatted_address}
                </span>
              </p>
            </div>
            {isMapsLoaded && (
              <GoogleMap
                zoom={15}
                center={{
                  lat: Number(event.latitude),
                  lng: Number(event.longitude),
                }}
                mapContainerClassName="w-full h-[200px] rounded-[12px]"
              >
                <Marker
                  position={{
                    lat: Number(event.latitude),
                    lng: Number(event.longitude),
                  }}
                />
              </GoogleMap>
            )}
          </BgWrapper>
        )}
      </div>
    </div>
  );
};
